import {
  ExclamationCircleFilled,
  EyeInvisibleOutlined,
  EyeOutlined,
} from '@ant-design/icons'
import classNames from 'classnames'
import { useMemo, useState } from 'react'
import {
  Control,
  Controller,
  FieldErrors,
  FieldValues,
  useFormContext,
  useWatch,
} from 'react-hook-form'
import MaskedInput, { type MaskedInputProps } from 'react-text-mask'

import { PHONE_INPUT_MASK } from '@/constants'
import type {
  FormInputControlType,
  FormInputLocationType,
  FormInputNameType,
  FormInputRegisterType,
} from '@/types'

import cn from './AtomFormInput.module.scss'
import CardfileInfo from './CardfileInfo/CardfileInfo'
import ExpandableInput from './ExpandableInput/ExpandableInput'

const LINKED_FIELDS: Partial<
  Record<FormInputNameType, Parameters<FormInputRegisterType>[0]>
> = {
  password: 'repeatPassword',
  repeatPassword: 'password',
  etaMinDays: 'etaMaxDays',
  etaMaxDays: 'etaMinDays',
}

type FormInputPropsType = Omit<
  React.DetailedHTMLProps<
    React.InputHTMLAttributes<HTMLInputElement>,
    HTMLInputElement
  >,
  'title'
> & {
  location: FormInputLocationType
  name: FormInputNameType
  title?: React.ReactNode
  errors?: FieldErrors
  control?: FormInputControlType
  register?: FormInputRegisterType
  setValue?: (
    name: never,
    value: never,
    options?: {
      shouldValidate?: boolean
      shouldDirty?: boolean
      shouldTouch?: boolean
    }
  ) => void
  generatePasswordCaller?: () => void
  handleClickReadOnlyInput?: () => void
  handleInputKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void
  handleChangeInput?: (e: React.ChangeEvent<HTMLInputElement>) => void
  preventAutofill?: boolean
  setValueAs?: (value: string) => unknown
  showExpandable?: boolean
  expandableValue?: string
  fullWidth?: boolean
  isOrganisationSelected?: boolean
  passwordVisible?: boolean
  onPasswordVisibleChange?: (visible: boolean) => void
  layout?: 'row' | 'stacked'
  idPrefix?: string
  mask?: MaskedInputProps['mask']
  secret?: boolean
  error?: string
  trailingText?: string
}

type NativeInputProps = React.InputHTMLAttributes<HTMLInputElement>

interface FormInputLogicProps {
  location: FormInputLocationType
  name: FormInputNameType
  showExpandable?: boolean
  expandableValue?: string
  register?: FormInputRegisterType
  setValueAs?: (value: string) => unknown
  handleChangeInput?: (e: React.ChangeEvent<HTMLInputElement>) => void
  placeholder?: string
  readOnly?: boolean
  setValue?: FormInputPropsType['setValue']
  hasError: boolean
  inputId: string
  errorId: string
  isStacked: boolean
  inputType: 'text' | 'password'
  handleFocus: (e: React.FocusEvent<HTMLInputElement>) => void
  isAutofillBlocked: boolean
  isPassword: boolean
  setShowPassword: (show: boolean) => void
  showPassword: boolean
  required?: boolean
  handleClickReadOnlyInput?: () => void
  handleInputKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void
  mask?: MaskedInputProps['mask']
  inputMode?: NativeInputProps['inputMode']
  onFocus?: NativeInputProps['onFocus']
  onBlur?: NativeInputProps['onBlur']
  trailingText?: string
}

interface InternalLogicProps {
  props: FormInputLogicProps
  control: FormInputControlType | Control<FieldValues>
  restProps: NativeInputProps
}

function FormInputRHFLogic({
  props,
  control: rawControl,
  restProps,
}: InternalLogicProps) {
  const control = rawControl as Control<FieldValues>
  const {
    location,
    name,
    showExpandable,
    expandableValue,
    register,
    setValueAs,
    handleChangeInput,
    placeholder,
    readOnly,
    setValue,
    hasError,
    inputId,
    errorId,
    isStacked,
    inputType,
    handleFocus,
    isAutofillBlocked,
    isPassword,
    setShowPassword,
    showPassword,
    required,
    handleClickReadOnlyInput,
    handleInputKeyDown,
    mask,
    inputMode,
    trailingText,
  } = props

  const watchedFieldValue = useWatch({
    control,
    name: name as string,
    defaultValue: expandableValue || '',
  })

  const fieldValue: unknown = useWatch({ control, name: name as string })

  const currentVatValue = useWatch({
    control,
    name: 'vatNumber',
    defaultValue: '',
  })

  const fieldRegister = register
    ? register(name as Parameters<FormInputRegisterType>[0], {
        ...(setValueAs ? { setValueAs } : {}),
        deps: LINKED_FIELDS[name],
        onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
          handleChangeInput?.(e)
          if (restProps.onChange) restProps.onChange(e)
        },
        onBlur: (e: React.FocusEvent<HTMLInputElement>) => {
          if (props.onBlur) props.onBlur(e)
        },
      })
    : null

  const watchedValue =
    showExpandable || isPassword ? watchedFieldValue : undefined

  const isPhone =
    (name === 'phoneNumber' || name === 'ownerPhoneNumber') &&
    ['register', 'userCreation', 'constructionSiteCreation'].includes(location)
  const inputMask = mask ?? (isPhone ? PHONE_INPUT_MASK : undefined)

  return (
    <>
      <div
        className={[
          cn.inputWrapper,
          isPassword ? cn.withEye : '',
          showExpandable ? cn.withExpandable : '',
        ].join(' ')}
      >
        {inputMask ? (
          <Controller
            name={name}
            control={control}
            rules={{ deps: LINKED_FIELDS[name] }}
            render={({ field }) => (
              <MaskedInput
                {...field}
                mask={inputMask}
                id={inputId}
                type={isPhone ? 'tel' : 'text'}
                inputMode={inputMode}
                guide={false}
                placeholder={isPhone ? '+375 (__) ___-__-__' : placeholder}
                aria-invalid={hasError}
                aria-describedby={errorId}
                className={classNames(cn.input, hasError && cn.inputInvalid)}
              />
            )}
          />
        ) : (
          <input
            {...restProps}
            {...fieldRegister}
            value={watchedValue !== undefined ? watchedValue : restProps.value}
            id={inputId}
            type={inputType}
            readOnly={readOnly || isAutofillBlocked}
            onFocus={handleFocus}
            placeholder={placeholder}
            onKeyDown={handleInputKeyDown}
            aria-invalid={hasError}
            aria-describedby={errorId}
            className={classNames(
              cn.input,
              hasError && cn.inputInvalid,
              isAutofillBlocked && cn.antiAutofill,
              showExpandable && cn.paddingForExpandable
            )}
            onClick={() => readOnly && handleClickReadOnlyInput?.()}
          />
        )}

        {showExpandable && fieldRegister && watchedValue !== undefined && (
          <ExpandableInput
            name={name as string}
            value={watchedValue}
            onChange={(e) => {
              const newValue = e.target.value
              if (setValue) {
                setValue(name as never, newValue as never, {
                  shouldValidate: true,
                  shouldDirty: true,
                  shouldTouch: true,
                })
              } else {
                fieldRegister.onChange(e)
              }
            }}
            readOnly={readOnly}
            placeholder={placeholder}
          />
        )}

        {trailingText && (
          <span className={cn.trailingText} aria-hidden>
            <span className={cn.trailingValue}>{String(fieldValue)}</span>
            {trailingText}
          </span>
        )}

        {isPassword && (
          <div
            className={classNames(cn.eyeIcon, hasError && cn.eyeIconInvalid)}
            onClick={() => setShowPassword(!showPassword)}
          >
            {showPassword ? <EyeInvisibleOutlined /> : <EyeOutlined />}
          </div>
        )}

        {location !== 'organisationEdit' &&
          location !== 'constructionSiteCreation' &&
          !isStacked &&
          required && <span className={cn.requiredStar}>*</span>}
      </div>

      {location === 'organisationEdit' && name === 'vatNumber' && (
        <CardfileInfo
          vatNumber={String(currentVatValue || restProps.value || '')}
        />
      )}
    </>
  )
}

function AtomFormInput(props: FormInputPropsType) {
  const {
    location,
    title,
    name,
    readOnly,
    errors,
    required,
    control: propsControl,
    register,
    setValue,
    generatePasswordCaller,
    handleClickReadOnlyInput,
    handleInputKeyDown,
    handleChangeInput,
    preventAutofill,
    setValueAs,
    showExpandable,
    expandableValue,
    fullWidth = false,
    isOrganisationSelected = false,
    passwordVisible,
    onPasswordVisibleChange,
    layout = 'row',
    idPrefix = '',
    mask,
    secret = false,
    error,
    trailingText,
    ...restProps
  } = props

  const methods = useFormContext()
  const control = propsControl || methods?.control

  const [localShowPassword, setLocalShowPassword] = useState<boolean>(false)
  const showPassword = passwordVisible ?? localShowPassword
  const setShowPassword = (visible: boolean) => {
    setLocalShowPassword(visible)
    onPasswordVisibleChange?.(visible)
  }
  const [isAutofillBlocked, setIsAutofillBlocked] = useState(!!preventAutofill)

  const isPassword = secret || name === 'password' || name === 'repeatPassword'
  const fieldErrorMessage = errors?.[name]?.message
  const errorText =
    error ?? (fieldErrorMessage ? String(fieldErrorMessage) : '')
  const hasError = errorText !== ''
  const inputId = `${idPrefix}input-${name}`
  const errorId = `${inputId}-error`
  const isStacked = layout === 'stacked'

  const inputType = useMemo((): 'text' | 'password' => {
    if (isPassword && !showPassword) return 'password'
    return 'text'
  }, [isPassword, showPassword])

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    if (isAutofillBlocked) setIsAutofillBlocked(false)
    if (props.onFocus) props.onFocus(e)
  }

  const logicProps = {
    location,
    name,
    showExpandable,
    expandableValue,
    register,
    setValueAs,
    handleChangeInput,
    placeholder: restProps.placeholder,
    readOnly,
    setValue,
    hasError,
    inputId,
    errorId,
    isStacked,
    inputType,
    handleFocus,
    isAutofillBlocked,
    isPassword,
    setShowPassword,
    showPassword,
    required,
    handleClickReadOnlyInput,
    handleInputKeyDown,
    mask,
    inputMode: restProps.inputMode,
    onFocus: props.onFocus,
    onBlur: props.onBlur,
    trailingText,
  }

  return (
    <div
      className={classNames(
        cn.inputsItem,
        fullWidth && cn.fullWidth,
        isOrganisationSelected && cn.inputsItemSelected,
        isStacked && cn.stacked
      )}
    >
      {title && (
        <label htmlFor={inputId}>
          {title}
          {isStacked && required && (
            <span className={cn.labelRequired} aria-hidden>
              *
            </span>
          )}
        </label>
      )}

      <div className={cn.rightColumn}>
        {control ? (
          <FormInputRHFLogic
            props={logicProps}
            control={control}
            restProps={restProps}
          />
        ) : (
          <div className={cn.inputWrapper}>
            <input
              {...restProps}
              id={inputId}
              type={inputType}
              readOnly={readOnly || isAutofillBlocked}
              onFocus={handleFocus}
              aria-invalid={hasError}
              aria-describedby={errorId}
              className={classNames(
                cn.input,
                hasError && cn.inputInvalid,
                isAutofillBlocked && cn.antiAutofill
              )}
            />
          </div>
        )}

        {location !== 'constructionSiteCreation' && (
          <span
            id={errorId}
            role={isStacked ? 'alert' : undefined}
            title={isStacked && hasError ? errorText : undefined}
            className={[cn.errorMessage, !hasError ? cn.hidden : ''].join(' ')}
          >
            {isStacked && hasError && (
              <ExclamationCircleFilled className={cn.errorIcon} />
            )}
            {errorText}
          </span>
        )}

        {(location === 'register' || location === 'userCreation') &&
          name === 'password' && (
            <input
              type="button"
              className={cn.generatePass}
              onClick={() => {
                generatePasswordCaller?.()
                setShowPassword(true)
              }}
              value="Сгенерировать"
            />
          )}
      </div>
    </div>
  )
}

export default AtomFormInput
